/**
 * Warranties — what warranty is active on which building, with a
 * maintenance-plan flag. "No Plan" rows are the maintenance sales list.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Download } from "lucide-react";

const TERMS = [10, 15, 20, 25];
const chip = (active) => `px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border rounded-sm transition-colors ${active ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`;

function TimeLeft({ state, months }) {
  if (months === null || months === undefined) return <span className="text-[10px] text-zinc-400">set start date</span>;
  if (state === "expired") return <span className="text-[10px] font-bold uppercase bg-zinc-100 text-zinc-500 border border-zinc-300 rounded-sm px-1.5 py-0.5">Expired</span>;
  const yrs = months / 12;
  const label = yrs >= 1 ? `${yrs.toFixed(1)} yrs left` : `${Math.max(0, Math.round(months))} mo left`;
  return <span className={`text-[10px] font-bold uppercase rounded-sm px-1.5 py-0.5 border ${state === "expiring" ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"}`}>{label}</span>;
}

function PlanFlag({ has }) {
  return has
    ? <span className="text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-300 rounded-sm px-1.5 py-0.5">On Plan</span>
    : <span className="text-[10px] font-bold uppercase bg-amber-50 text-amber-800 border border-amber-400 rounded-sm px-1.5 py-0.5">No Plan</span>;
}

export default function Warranties() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/reports/warranties").then((r) => setData(r.data)).catch(() => setData({ tracked: [], untracked: [], kpis: {} }));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const t = data?.tracked || [];
    if (filter === "noplan") return t.filter((r) => !r.maintenance_plan && r.state !== "expired");
    if (filter === "expiring") return t.filter((r) => r.state === "expiring");
    if (filter === "expired") return t.filter((r) => r.state === "expired");
    return t;
  }, [data, filter]);

  const exportCallList = () => {
    const src = (data?.tracked || []).filter((r) => !r.maintenance_plan && r.state !== "expired");
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = src.map((r) => [esc(r.customer), esc(r.title), esc(r.address), esc(r.phone), esc(r.email),
      `${r.term_years}-yr ${r.warranty_type}`.trim(), r.expires || ""].join(","));
    const blob = new Blob(["Customer,Building,Address,Phone,Email,Warranty,Expires\n" + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `maintenance_prospects_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!data) return <div className="p-8 text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</div>;
  const k = data.kpis || {};

  return (
    <div className="p-6 lg:p-8" data-testid="warranties-page">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Warranties</h1>
      </div>
      <p className="text-xs text-zinc-500 mb-5">Every active warranty by building. Buildings without a maintenance plan are your sales list — most manufacturer warranties require documented maintenance to stay valid.</p>

      <div className="grid grid-cols-3 gap-3 max-w-xl mb-5">
        {[["Active Warranties", k.active, "text-zinc-900"], ["Expiring ≤ 12 Mo", k.expiring_12mo, "text-amber-700"], ["No Maintenance Plan", k.no_plan, "text-amber-800"]].map(([label, v, tone]) => (
          <div key={label} className="bg-white border border-zinc-200 rounded-sm p-3 text-center">
            <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
            <div className={`text-2xl font-black ${tone}`}>{v ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button className={chip(filter === "all")} onClick={() => setFilter("all")} data-testid="wf-all">All</button>
        <button className={chip(filter === "noplan")} onClick={() => setFilter("noplan")} data-testid="wf-noplan">No Maintenance Plan</button>
        <button className={chip(filter === "expiring")} onClick={() => setFilter("expiring")} data-testid="wf-expiring">Expiring ≤ 12 Mo</button>
        <button className={chip(filter === "expired")} onClick={() => setFilter("expired")} data-testid="wf-expired">Expired</button>
        <button onClick={exportCallList} className="ml-auto inline-flex items-center gap-1 h-8 px-3 text-[10px] font-bold uppercase tracking-wider bg-zinc-950 text-white rounded-sm hover:bg-zinc-800" data-testid="wf-export">
          <Download className="w-3 h-3" /> Export No-Plan Call List
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto mb-8">
        <table className="w-full text-sm" data-testid="warranties-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
              <th className="px-4 py-2.5">Building</th>
              <th className="px-3 py-2.5">Customer</th>
              <th className="px-3 py-2.5">Warranty</th>
              <th className="px-3 py-2.5">Start</th>
              <th className="px-3 py-2.5">Expires</th>
              <th className="px-3 py-2.5">Time Left</th>
              <th className="px-4 py-2.5">Maintenance</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-zinc-400">Nothing here — add warranty info below or change the filter.</td></tr>}
            {rows.map((r) => (
              <tr key={r.deal_id} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid={`warranty-row-${r.deal_id}`}>
                <td className="px-4 py-2.5">
                  <Link to={`/projects/${r.deal_id}`} className="font-bold hover:text-blue-700">{r.title}</Link>
                  {r.address && <div className="text-[11px] text-zinc-500">{r.address}</div>}
                </td>
                <td className="px-3 py-2.5">
                  {r.customer}
                  {(r.phone || r.email) && <div className="text-[11px] text-zinc-500">{[r.phone, r.email].filter(Boolean).join(" · ")}</div>}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs font-bold">{r.term_years}-yr {r.warranty_type}{r.manufacturer ? ` · ${r.manufacturer}` : ""}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{r.start_date || "—"}</td>
                <td className="px-3 py-2.5 font-mono text-xs">{r.expires || "—"}</td>
                <td className="px-3 py-2.5"><TimeLeft state={r.state} months={r.months_left} /></td>
                <td className="px-4 py-2.5"><PlanFlag has={r.maintenance_plan} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(data.untracked || []).length > 0 && (
        <>
          <h2 className="text-base font-black tracking-tight mb-1">Jobs Missing Warranty Info</h2>
          <p className="text-xs text-zinc-500 mb-3">Won/completed jobs without a recorded warranty — fill them in right here.</p>
          <div className="bg-white border border-dashed border-zinc-300 rounded-sm divide-y divide-zinc-50" data-testid="untracked-list">
            {data.untracked.map((r) => <UntrackedRow key={r.deal_id} r={r} onSaved={load} />)}
          </div>
        </>
      )}
    </div>
  );
}

function UntrackedRow({ r, onSaved }) {
  const [term, setTerm] = useState("");
  const [type, setType] = useState("Standard");
  const [start, setStart] = useState(r.suggested_start || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!term) { toast.error("Pick a term"); return; }
    setBusy(true);
    try {
      await api.patch(`/deals/${r.deal_id}/warranty`, { warranty_term_years: Number(term), warranty_type: type, warranty_start_date: start });
      toast.success("Warranty recorded");
      onSaved();
    } catch { toast.error("Save failed"); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5" data-testid={`untracked-row-${r.deal_id}`}>
      <div className="min-w-[220px] flex-1">
        <Link to={`/projects/${r.deal_id}`} className="font-bold text-sm hover:text-blue-700">{r.title}</Link>
        <span className="text-[11px] text-zinc-500 ml-2">{r.customer}</span>
      </div>
      <select value={term} onChange={(e) => setTerm(e.target.value)} className="h-8 px-2 border border-zinc-300 rounded-sm text-xs bg-white" data-testid={`uw-term-${r.deal_id}`}>
        <option value="">Term…</option>
        {TERMS.map((t) => <option key={t} value={t}>{t}-year</option>)}
      </select>
      <select value={type} onChange={(e) => setType(e.target.value)} className="h-8 px-2 border border-zinc-300 rounded-sm text-xs bg-white" data-testid={`uw-type-${r.deal_id}`}>
        <option>Standard</option><option>NDL</option><option>Labor Only</option><option>Material Only</option>
      </select>
      <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-8 px-2 border border-zinc-300 rounded-sm text-xs bg-white" data-testid={`uw-start-${r.deal_id}`} />
      <button disabled={busy} onClick={save} className="h-8 px-3 text-[10px] font-bold uppercase tracking-wider bg-blue-700 text-white rounded-sm hover:bg-blue-800 disabled:opacity-50" data-testid={`uw-save-${r.deal_id}`}>Save</button>
    </div>
  );
}
